import {Dayjs} from 'dayjs';
import {z as r} from 'zod';

import {generateResponseSchema} from '../api/api-type';

export enum CommonEventStatusEnum {
    Deleted = 'deleted',
    Draft = 'draft',
    Planned = 'planned',
    InProgress = 'in_progress',
    Sent = 'sent',
}

export enum EventCatalogStatusEnum {
    Unknown = '',
    Active = 'active',
    Planned = 'planned',
    Finished = 'finished',
    Deleted = 'deleted',
    OnModeration = 'on_moderation',
    Rejected = 'rejected',
}

export enum EventTypeEnum {
    Concert = 'concert',
    Cinema = 'cinema',
    Children = 'children',
    Exhibition = 'exhibition',
    Sports = 'sports',
    Excursion = 'excursion',
    StandUp = 'stand_up',
    Show = 'show',
    Quest = 'quest',
    Musical = 'musical',
    MasterClass = 'master_class',
    Lecture = 'lecture',
    ThematicDinner = 'thematic_dinner',
    Tasting = 'tasting',
    Training = 'training',
    Webinar = 'webinar',
    Conference = 'conference',
    Forum = 'forum',
    Occasion = 'occasion',
}

export enum EventAgeEnum {
    Age0 = 0,
    Age6 = 6,
    Age12 = 12,
    Age16 = 16,
    Age18 = 18,
    Age21 = 21,
}

export const EVENT_AGE_LIST: Array<EventAgeEnum> = [
    EventAgeEnum.Age0,
    EventAgeEnum.Age6,
    EventAgeEnum.Age12,
    EventAgeEnum.Age16,
    EventAgeEnum.Age18,
    EventAgeEnum.Age21,
];

export const EVENT_TYPE_LIST: Array<EventTypeEnum> = [
    EventTypeEnum.Concert,
    EventTypeEnum.Cinema,
    EventTypeEnum.Children,
    EventTypeEnum.Exhibition,
    EventTypeEnum.Sports,
    EventTypeEnum.Excursion,
    EventTypeEnum.StandUp,
    EventTypeEnum.Show,
    EventTypeEnum.Quest,
    EventTypeEnum.Musical,
    EventTypeEnum.MasterClass,
    EventTypeEnum.Lecture,
    EventTypeEnum.ThematicDinner,
    EventTypeEnum.Tasting,
    EventTypeEnum.Training,
    EventTypeEnum.Webinar,
    EventTypeEnum.Conference,
    EventTypeEnum.Forum,
    EventTypeEnum.Occasion,
];

const eventListItemSchema = r.object({
    id: r.number(),
    title: r.string(),
    status: r.nativeEnum(CommonEventStatusEnum),
    eventType: r.nativeEnum(EventTypeEnum),
    minAge: r.nativeEnum(EventAgeEnum),
    preview: r.object({id: r.number(), url: r.string()}).nullable(),
    companiesCount: r.number(),
    createdAt: r.string().nullable(),
    updatedAt: r.string().nullable(),
    publishAt: r.string().nullable(),
    deletedAt: r.string().nullable(),
    startAt: r.string().nullable(),
    finishAt: r.string().nullable(),
});

export type EventListItemType = r.infer<typeof eventListItemSchema>;

export const eventsListResponseSchema = generateResponseSchema(eventListItemSchema);

export type EventsListResponseType = r.infer<typeof eventsListResponseSchema>;

const eventImageSchema = r.object({
    id: r.number(),
    url: r.string(),
});

type EventImageType = r.infer<typeof eventImageSchema>;

export const eventDetailSchema = eventListItemSchema.merge(
    r.object({
        description: r.string(),
        companiesIds: r.array(r.number()),
        catalogsIds: r.array(r.number()),
        images: r.array(eventImageSchema),
    })
);

export type EventDetailType = r.infer<typeof eventDetailSchema>;

const eventSelectedCompanySchema = r.object({
    id: r.number(),
    status: r.nativeEnum(EventCatalogStatusEnum),
    name: r.string(),
    address: r.string().nullable(),
    companyCode: r.string().optional(),
});

export type EventSelectedCompanyType = r.infer<typeof eventSelectedCompanySchema>;

export const eventSelectedCompaniesSchema = generateResponseSchema(eventSelectedCompanySchema);

export type EventSelectedCompaniesType = r.infer<typeof eventSelectedCompaniesSchema>;

export const uploadEventImageResponseSchema = r.object({
    id: r.number(),
});

export type UploadEventImageResponseType = r.infer<typeof uploadEventImageResponseSchema>;

// Payload sent to the API (camelCase; `serialize` converts it to snake_case).
// Companies are resolved server-side from the posts companies-selector via `selectorId`.
export type EventRequestType = {
    title: string;
    description: string;
    eventType: EventTypeEnum;
    minAge: EventAgeEnum;
    selectorId: string;
    catalogsIds: Array<number>;
    publishAt: string | null;
    deletedAt: string | null;
    startAt: string;
    finishAt: string;
    imageIds: Array<number>;
    status: CommonEventStatusEnum;
};

export enum EventFormFieldsEnum {
    Title = 'title',
    Description = 'description',
    EventType = 'eventType',
    MinAge = 'minAge',
    Images = 'images',
    Period = 'period',
}

// The event's active period as rendered by a single range picker.
type EventPeriodType = [Dayjs | null, Dayjs | null] | null;

// Form shape. Selected companies live in the companies-selector (not the form), the form only keeps
// the brand and the mirrored selected count so both can be validated.
export type EventFormType = {
    title: string;
    description?: string;
    eventType?: EventTypeEnum;
    minAge?: EventAgeEnum;
    images: Array<EventImageType>;
    period: EventPeriodType;
    brandId?: number | null;
    companiesCount?: number;
    // Carried through untouched: no longer editable, but an update must not wipe them.
    publishAt?: Dayjs | null;
    deletedAt?: Dayjs | null;
};
